<template>
  <transition
    name="message"
    appear
  >
    <div
      v-show="visible"
      class="p-message"
      :class="['p-message-'+severity, { 'no-borders': !hasBorders }]"
      :role="ariaRole"
    >
      <div class="p-message-wrapper">
        <span
          class="p-message-icon"
          :class="severity + '-icon'"
        />
        <div
          class="p-message-text"
        >
          <slot />
        </div>
        <button
          v-if="closable"
          aria-label="close"
          class="p-message-close"
          @click="close($event)"
        >
          <i class="p-message-close-icon" />
        </button>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'Notification',
  props: {
    severity: {
      type: String,
      default: 'info',
      validator: (val) => ['info', 'success', 'error'].includes(val),
    },
    closable: {
      type: Boolean,
      default: true,
    },
    hasBorders: {
      type: Boolean,
      default: true,
    },
    ariaRole: {
      type: String,
      default: undefined,
    },
  },
  emits: ['close'],
  data() {
    return {
      visible: true,
    };
  },
  methods: {
    close(event) {
      this.visible = false;
      this.$emit('close', event);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@csgIcons/_tooltip-large.scss";
@import "~@csgIcons/_error-red.scss";
@import "~@csgIcons/_close-small.scss";
@import "@core/csg/src/scss/typography/typography-mixins.scss";

.p-message {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: rem-calc(10);
  border-radius: $global-border-radius;
  background: $white;
  &.p-message-info {
    border: rem-calc(1) solid $black;
    color: $black;
  }
  &.p-message-success {
    border: 1px solid $green;
    color: $green;
  }
  &.p-message-error {
    border: 1px solid $error-color;
    color: $error-color;
  }
  &.no-borders {
    border: 0;
    padding: 0;
    margin: 0;
    gap: 0;
    .p-message-wrapper {
      gap: rem-calc(8);
      padding: 0;
      .p-message-text {
        padding-top: rem-calc(3);
        @include body(small);
        color: inherit;
      }
    }
  }
  .p-message-wrapper {
    display: flex;
    padding: rem-calc(16);
    align-items: flex-start;
    gap: rem-calc(12);
    align-self: stretch;
    .info-icon {
      @extend .tooltip-large;
    }
    .error-icon {
      @extend .error-red;
    }
    .p-message-text {
      @include body(regular);
      color: inherit;
      padding-top: rem-calc(2);
      flex: 1 0 0;
      :deep(*){
        color: inherit;
      }
    }
    .p-message-close {
      cursor: pointer;
      display: flex;
      .p-message-close-icon {
        @extend .close-small;
      }
    }
  }
}
.message-enter-from {
  opacity: 0;
}
.message-enter-active {
  transition: opacity 0.3s;
}
.message-leave-from {
  max-height: 1000px;
}
.message-leave-to {
  max-height: 0;
  opacity: 0;
  margin: 0 !important;
}
.message-leave-active {
  overflow: hidden;
  transition: max-height 0.3s cubic-bezier(0, 1, 0, 1), opacity 0.3s,
    margin 0.15s;
}
.message-leave-active .p-message-close {
  display: none;
}
</style>

<style lang="scss" brand="mcom" scoped>
@import "~@csgIcons/_confirmation-green-large.scss";
.p-message {
  .p-message-wrapper {
    .success-icon {
      @extend .confirmation-green-large;
    }
  }
}
</style>

<style lang="scss" brand="bcom" scoped>
@import "~@csgIcons/_confirmation-green-checkmark.scss";
.p-message {
  &.no-borders {
    .p-message-wrapper {
      .p-message-text {
        padding-top: rem-calc(4);
      }
    }
  }
  .p-message-wrapper {
    .success-icon {
      @extend .confirmation-green-checkmark;
    }
    .p-message-text {
      padding-top: rem-calc(3);
    }
  }
}
</style>
